import { Routes } from '@angular/router';
import { AuthGuard } from '@guards/auth.guard';
import { OnboardingRedirectGuard } from '@guards/onboarding-redirect.guard';

/**
 * SPECIALTY DETAILS ROUTES
 * Routes for specialty details page with tabs (dashboard, specialists, things, grades, etc.)
 */
export const SPECIALTY_DETAILS_ROUTES: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('@features/library/specialties/specialty-details/specialty-details.component').then(
        m => m.SpecialtyDetailsComponent
      ),
    canActivate: [AuthGuard, OnboardingRedirectGuard],
    data: {
      breadcrumb: 'Специальность',
      roles: ['user'],
    },
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        loadComponent: () =>
          import(
            '@features/library/specialties/specialty-details/components/library-specialty-dashboard/library-specialty-dashboard.component'
          ).then(m => m.LibrarySpecialtyDashboardComponent),
        title: 'Дашборд',
        data: {
          breadcrumb: 'Дашборд',
          roles: ['user'],
        },
        canActivate: [AuthGuard, OnboardingRedirectGuard],
      },
      {
        path: 'specialists',
        loadComponent: () =>
          import(
            '@features/library/specialties/specialty-details/components/library-specialty-specialists/library-specialty-specialists.component'
          ).then(m => m.LibrarySpecialtySpecialistsComponent),
        title: 'Специалисты',
        data: {
          breadcrumb: 'Специалисты',
          roles: ['user'],
        },
        canActivate: [AuthGuard, OnboardingRedirectGuard],
      },
      {
        path: 'things',
        loadComponent: () =>
          import(
            '@features/library/specialties/specialty-details/components/library-specialty-things/library-specialty-things.component'
          ).then(m => m.LibrarySpecialtyThingsComponent),
        title: 'Навыки',
        data: {
          breadcrumb: 'Навыки',
          roles: ['user'],
        },
        canActivate: [AuthGuard, OnboardingRedirectGuard],
      },
      {
        path: 'grades',
        loadComponent: () =>
          import(
            '@features/library/specialties/specialty-details/components/library-specialty-grades/library-specialty-grades.component'
          ).then(m => m.LibrarySpecialtyGradesComponent),
        title: 'Грейды',
        data: {
          breadcrumb: 'Грейды',
          roles: ['user'],
        },
        canActivate: [AuthGuard, OnboardingRedirectGuard],
      },
      {
        path: 'specialty-in-teams',
        loadComponent: () =>
          import(
            '@features/library/specialties/specialty-details/components/library-specialty-things-in-teams/library-specialty-things-in-teams.component'
          ).then(m => m.LibrarySpecialtyThingsInTeamsComponent),
        title: 'Специальность в командах',
        data: {
          breadcrumb: 'Специальность в командах',
          roles: ['org_admin', 'admin'],
        },
        canActivate: [AuthGuard, OnboardingRedirectGuard],
      },
      {
        path: 'specialty-compliance',
        loadComponent: () =>
          import(
            '@features/library/specialties/specialty-details/components/library-specialty-compliance/library-specialty-compliance.component'
          ).then(m => m.LibrarySpecialtyComplianceComponent),
        title: 'Соответствие специальности',
        data: {
          breadcrumb: 'Соответствие специальности',
          roles: ['user'],
        },
        canActivate: [AuthGuard, OnboardingRedirectGuard],
      },
    ],
  },
];

/**
 * DOMAIN DETAILS ROUTES (THING DETAILS)
 * Routes for thing/domain details page
 */
export const DOMAIN_DETAILS_ROUTES: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('@features/library/domains/domain-details/domain-details.component').then(
        m => m.DomainDetailsComponent
      ),
    data: {
      breadcrumb: 'Навык',
    },
    canActivate: [AuthGuard, OnboardingRedirectGuard],
  },
  {
    path: 'merge-duplicates',
    loadComponent: () =>
      import(
        '@features/library/domains/domain-details/components/thing-merge-dublicates/thing-merge-duplicates.component'
      ).then(m => m.ThingMergeDuplicatesComponent),
    data: {
      breadcrumb: 'Объединение дубликатов навыка',
    },
    canActivate: [AuthGuard, OnboardingRedirectGuard],
  },
];

/**
 * NEW LIBRARY ROUTES
 * Routes for the new library UI (features/library)
 * LibraryComponent serves as wrapper with tabs navigation
 */
export const LIBRARY_NEW_ROUTES: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('@features/library/library.component').then(m => m.LibraryComponent),
    data: {
      breadcrumb: 'Библиотека',
      roles: ['user'],
    },
    canActivate: [AuthGuard, OnboardingRedirectGuard],
    children: [
      {
        path: '',
        redirectTo: 'domains',
        pathMatch: 'full',
      },
      {
        path: 'domains',
        loadComponent: () =>
          import('@features/library/domains/domains.component').then(m => m.DomainsComponent),
        title: 'Навыки',
        data: {
          breadcrumb: 'Навыки',
          roles: ['user'],
        },
        canActivate: [AuthGuard, OnboardingRedirectGuard],
      },
      {
        path: 'specialties',
        loadComponent: () =>
          import('@features/library/specialties/specialties.component').then(
            m => m.SpecialtiesComponent
          ),
        title: 'Специальности',
        data: {
          breadcrumb: 'Специальности',
          roles: ['user'],
        },
        canActivate: [AuthGuard, OnboardingRedirectGuard],
      },
      {
        path: 'specialists-search',
        loadComponent: () =>
          import('@features/library/specialistsSearch/specialists-search.components').then(
            m => m.SpecialistsSearchComponent
          ),
        title: 'Подбор специалистов',
        data: {
          breadcrumb: 'Подбор специалистов',
          roles: ['user'],
        },
        canActivate: [AuthGuard, OnboardingRedirectGuard],
      },
    ],
  },
  // Specialty details routes
  {
    path: 'specialties/:specialtyId',
    canActivate: [AuthGuard, OnboardingRedirectGuard],
    children: SPECIALTY_DETAILS_ROUTES,
  },
  // Domain/Thing details routes
  {
    path: 'domains/:domainId/things/:thingId',
    canActivate: [AuthGuard, OnboardingRedirectGuard],
    children: DOMAIN_DETAILS_ROUTES,
  },
];

/**
 * LIBRARY ROUTES
 * Main library route controlled by feature flag 'enableLibraryUIV2'
 * This route will be dynamically replaced by initializeFeatureFlags:
 * - Old library: routes/library-old.routes.ts (LIBRARY_OLD_ROUTES)
 * - New library: routes/library.routes.ts (LIBRARY_NEW_ROUTES)
 */
export const LIBRARY_ROUTES: Routes = [
  {
    path: 'library',
    children: [], // Will be replaced by feature flag initializer with LIBRARY_NEW_ROUTES or LIBRARY_OLD_ROUTES
  },
];
